import { useEffect, useCallback, useMemo } from 'react';
import { DesignConstants } from '../utilities/DesignConstants';

import { setOverlayWithDelayInvoker, setSelectedTabIndexInvoker } from '../utilities/StoreLibrary';
import ActionTypes from '../base/ActionTypes';


import { useDispatch, useSelector } from '../context/Store/StoreHooks';
import { useTranslation } from '../context/Translation/TranslationHooks';

import PageComponentData from '../models/PageComponentData';

// Import required components
import { encodeUTF8, mergeObjects, resolveAbsolutePath } from '../utilities/UtilityLibrary';


import RefreshIcon from '@mui/icons-material/Refresh';

import { BlockListField } from '../fieldcomponents/BlockListField';
import { ToggleBlockListField } from '../fieldcomponents/ToggleBlockListField';
import { CustomButtonField } from '../fieldcomponents/CustomButtonField';
import { CheckBoxField } from '../fieldcomponents/CheckBoxField';
import { ComboBoxField } from '../fieldcomponents/ComboBoxField';
import { TextBoxField } from '../fieldcomponents/TextBoxField';
import { BlockTextBoxField } from '../fieldcomponents/BlockTextBoxField';
import { ExpandableComboBoxField } from '../fieldcomponents/ExpandableComboBoxField';
import { ConstantTextBoxField } from '../fieldcomponents/ConstantTextBoxField';
import { ConstantListBoxField } from '../fieldcomponents/ConstantListBoxField';
import { LabelField } from '../fieldcomponents/LabelField';
import { SmartListRowField } from '../fieldcomponents/SmartListRowField';
import { TabPageIconField } from '../fieldcomponents/TabPageIconField';

import { Box, Button, DialogActions, DialogContent, IconButton, Stack, Tooltip } from '@mui/material';
import { DialogWithBackdropClick } from '../dialogs/DialogWithBackdropClick';
import { StyledDialogTitle } from '../controls/StyledDialogTitle/StyledDialogTitle';
import { NoDisplayField } from '../fieldcomponents/NoDisplayField';

import { SmartListField } from '../fieldcomponents/SmartListField';
import { MemoryStoreLibrary } from '../utilities/MemoryStoreLibrary';
import { styled } from '@mui/material/styles';

// Import required components
import { LoadSpinner } from '../dialogs/LoadSpinner';
import { CalendarRulesSmartListField } from '../fieldcomponents/CalendarRulesSmartListField';
import { CompositeDataBuilderFormField } from '../fieldcomponents/CompositeDataBuilderFormField';
import { DialogTabBarField } from '../dialogs/DialogTabBarField';
import { useVrsTranslationState } from '../../../context/AppContext/AppContext';
import { DialogOverlay, DialogTabBarContainer, MainDialogContainer, PreviewImgContainer, TabBar } from './DesignDialogStyles';
import { useDesignDialogActionsContext } from './DesignDialogActionsContext';
import { IAction } from '../interfaces/IAction';



const StyledDialogWithBackdropClick = styled(DialogWithBackdropClick)(() => ({
    padding: 0,
    fontSize: "medium"
}));

const StyledDialogActions = styled(DialogActions)(() => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
}));

interface DesignDialogProps{
    actionFromParent?: IAction;
}

export const DesignDialog = ({actionFromParent}: DesignDialogProps) => {
    const dispatch = useDispatch();
    const changeBlockDialogStatus = useCallback((block: boolean) => {
        dispatch({ type: ActionTypes.STORE_UpdateIsDialogBlockedAction, payload: block });
    }, [dispatch]);

    // Initialize useDesignDialogActions with required props
    const {
        designDialogShowErrorBox,
        showMessageBox,
        designDialogShowUserLevelWarningDialog,

        // Data Management
        designDialogCollectDialogData,
        designDialogLoadInitialDialogData,

        // Preview
        designDialogPreviewDialog,

        // AI Properties
        getNewAiProperties,
        designDialogCloseDialog,
        designDialogRefreshDialogWithStackedDialogWithTheSameDialogType,
        designDialogAddNewField,
        extractExceptionName,
        designDialogPageControlValidationState,
        designDialogRefreshPage,
        designDialogRenderTab,
        getExistingAiProperties,
        collectMergeFieldDataToSendToServer,
        previewDialog,
        executeButtonLink,
        designDialogHandleButtonClick,
        dialogOpenIndex,
        setDialogOpenIndex,
        isDialogOpen,
        setIsDialogOpen,
        dialogName,
        setDialogName,

        setIsDialogVisible,
        baseUrl,
        setBaseUrl,

        // Dialog Lifecycle States
        initateDisplayComponents,
        setInitateDisplayComponents,
        isDialogClosing,
        setIsDialogClosing,
        pageRefreshed,
        setPageRefreshed,
        previousActionFromParent,
        setPreviousActionFromParent,

        // Data Management States

        everyRequestPostData,
        fieldNameShadowCopy,
        setFieldNameShadowCopy,

        setParsedDialogData,

        // UI Control States

        setTabBarVisible,
        okButtonDefinition,
        closeButtonDefinition,

        setSelectedTabBarIconIndex,
        subAddFieldDialogOpened,
        setSubAddFieldDialogOpened,

        // Validation and Error States
        controlErrorFlags,
        isDialogInError,
        setIsDialogInError,

        // Dialog Type Specific States
        setDialogSingleStemActionType,

        // Preview Related States
        setPreviewImageUrl,
        previewWorkflowIndex,
        setPreviewWorkflowIndex,

        // Block Management States
        setAiBlocksReordered,


        // User Input States
        isUserEditable,
        setIsUserEditable,
        isFixedLength,
        setIsFixedLength,

        // Warning/Message States
        defaultUserLevelWarningTitle,
        defaultUserLevelWarningMessage,

        // Refs
        stackedDialogs,

        // Computed States
        previewAvailableInDialog,

        // Helper properties from dialogProps
        extraHandlerOnCancel,

        // Utility Functions
        closeDialog,
        openDialog,
        designDialogProcessComponentsData,
        resolveTheSameLevelActions,

        isDialogVisible,
        maxWidth,
        pageTitle,
        tabBarVisible,
        dialogTabBarProps,

        selectedTabBarIconIndex,
        previewImageUrl,

        parsedDialogData,
        PropertyCollectionDictionary,
        updatePropertyCollectionDictionary,
        refreshIndex,
        mergeFieldDialog,
        dialogStateToPropsObservable,
        runSimpleAjaxCommandAsync,
        sendActionToParent,
        sendActionToChildren,


    } = useDesignDialogActionsContext();

    const { _T } = useVrsTranslationState();
    const { getTranslatedString } = useTranslation();

    const initialAddFieldXPos = useSelector(state => state.dialogState.initialAddFieldXPos);
    const initialAddFieldYPos = useSelector(state => state.dialogState.initialAddFieldYPos);
    const doesDialogHaveError = useSelector((state) => state.dialogState.doesDialogHaveError);

    const designDialogButtonLinkResolver = useCallback((controlDefinition) => {

        // Action buttons, MIGHT have a result property on them, if they do, then we need to examine it
        // to see if it has the old "key" and "value" properties, if it does we assemble those into a coherant KVP object
        // if the result property doesn't exist, then we create one for use in FE-Code only.  We do this mainly so we
        // have somewhere to place any post data in the link property, prior to sending it back next req we make
        if (controlDefinition["Result"]) {
            const tempResult = controlDefinition.Result;
            if (tempResult["Key"] && tempResult["Value"]) {
                const tempKvp = {};
                tempKvp[controlDefinition.Result.Key] = controlDefinition.Result.Value;
                controlDefinition.Result = mergeObjects(controlDefinition.Result, tempKvp);
            }
        } else {
            controlDefinition["Result"] = {}; // If our control doesnt have a result already, we create one
        }

        const linkParts = controlDefinition.Link.split('|');
        const commandText = linkParts.shift(); // grab our CMD_XXXXXXX part and remove it from the array of link parts

        // Now the actual deserialization happens
        for (const linkPart of linkParts) {
            const dataParts = linkPart.split(":");
            controlDefinition.Result[dataParts[0]] = dataParts.slice(1).join(":"); // Anything remaining get's combined back to a JSON string or a single regular string if only 1 element
        }

        // At this point we should now have our command in "commandText" and all of the data provided to that command in "result"

        if (commandText === DesignConstants.SaveCommand) {
            // Set the buttons REAL link to point to the ACTUAL rest endpoint we want it to talk to if this button is clicked
            controlDefinition.Link = baseUrl; // + DesignConstants.linkSuffixSave;
            controlDefinition.HandlingType = DesignConstants.linkSuffixSave;
        }

        if (commandText === DesignConstants.CloseCommand) {
            // Set the buttons REAL link to point to the ACTUAL rest endpoint we want it to talk to if this button is clicked
            controlDefinition.Link = "";
        }

        if (commandText === DesignConstants.RevertCommand) {
            // Set the buttons REAL link to point to the ACTUAL rest endpoint we want it to talk to if this button is clicked
            controlDefinition.Link = baseUrl;
            controlDefinition.HandlingType = DesignConstants.linkSuffixRevert;
        }

        if (commandText === DesignConstants.RefreshCommand) {
            // This is just temp for now until we fully have the BE building the dialogs for us
            controlDefinition.Page = controlDefinition.Result.PageName;

            // Set the buttons REAL link to point to the ACTUAL rest endpoint we want it to talk to if this button is clicked
            controlDefinition.Link = baseUrl;
            controlDefinition.HandlingType = DesignConstants.linkSuffixRefresh;
        }

        if (commandText === DesignConstants.DialogCommand) {
            controlDefinition.OpensDialog = true;

            // This is just temp for now until we fully have the BE building the dialogs for us
            controlDefinition.Link = controlDefinition.Result.DialogName;
        }

        if (commandText === DesignConstants.FunctionCommand) {
            controlDefinition.CallsFunction = true;

            // This is just temp for now until we fully have the BE building the dialogs for us
            controlDefinition.Link = controlDefinition.Result.FunctionName;
        }
    }, [baseUrl]);

    const designDialogUpdateOffsetDate = useCallback(async (dispatch, getTranslatedString, editorTemplateUrl: string, _: string, dataOffsetId: string) => {
        changeBlockDialogStatus(true);

        //Add the previous dialog to the dialog stack
        stackedDialogs.current.push({
            DialogUrl: resolveAbsolutePath(editorTemplateUrl),
            DialogControlName: "generic",
            PostData: {
                CiffAction: {
                    ActionType: "GetDateOffsetsProps",
                    ActionPayload: {
                        SingleActionObject: {
                            CiffName: MemoryStoreLibrary.getString("ciffName"),
                            SubImage: MemoryStoreLibrary.getString("subImage")
                        }
                    }
                }
            },
            DialogSingleStemActionType: "DateOffsetsProps",
        });

        try {
            const data = await runSimpleAjaxCommandAsync({
                AjaxType: "PostJson",
                Action: "GetDateOffset",
                AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                AjaxData: {
                    CiffAction: {
                        ActionType: "GetDateOffsetProps",
                        ActionPayload: {
                            SingleActionObject: {
                                SubImage: MemoryStoreLibrary.getString("subImage"),
                                DateOffsetID: dataOffsetId
                            }
                        }
                    }
                }
            });

            data.SingleStemActionType = "DateOffsetProps";
            setBaseUrl(editorTemplateUrl);
            setSelectedTabIndexInvoker(dispatch, 0);
            designDialogProcessComponentsData(data);
            changeBlockDialogStatus(false);
        } catch (xhr: any) {
            showMessageBox('Error', xhr.status ? getTranslatedString("CE_DataBuilderDataReadError").replace("%1", xhr.status.toString()) : xhr.toString());
            changeBlockDialogStatus(false);
        }

    }, [changeBlockDialogStatus, runSimpleAjaxCommandAsync, designDialogProcessComponentsData, showMessageBox]);

    const designDialogUpdateMarkSet = useCallback(async (dispatch, getTranslatedString, editorTemplateUrl: string, _: string, markSetId: string) => {
        changeBlockDialogStatus(true);

        //Add the previous dialog to the dialog stack
        stackedDialogs.current.push({
            DialogUrl: resolveAbsolutePath(editorTemplateUrl),
            DialogControlName: "generic",
            PostData: {
                CiffAction: {
                    ActionType: "GetMarkSetsProps",
                    ActionPayload: {
                        SingleActionObject: {
                            CiffName: MemoryStoreLibrary.getString("ciffName"),
                            SubImage: MemoryStoreLibrary.getString("subImage")
                        }
                    }
                }
            },
            DialogSingleStemActionType: "MarkSetsProps",
        });

        try {
            const data: any = await runSimpleAjaxCommandAsync({
                AjaxType: "PostJson",
                Action: "GetMarkSet",
                AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                AjaxData: {
                    CiffAction: {
                        ActionType: "GetMarkSetProps",
                        ActionPayload: {
                            SingleActionObject: {
                                SubImage: MemoryStoreLibrary.getString("subImage"),
                                MarkSetID: markSetId
                            }
                        }
                    }
                }
            });
            data.SingleStemActionType = "MarkSetProps";
            setBaseUrl(editorTemplateUrl);
            setSelectedTabIndexInvoker(dispatch, 0);
            designDialogProcessComponentsData(data);
            changeBlockDialogStatus(false);
        } catch (xhr: any) {
            showMessageBox('Error', xhr.status ? getTranslatedString("CE_DataBuilderDataReadError").replace("%1", xhr.status.toString()) : xhr.toString());
            changeBlockDialogStatus(false);
        }
    }, [changeBlockDialogStatus, runSimpleAjaxCommandAsync, designDialogProcessComponentsData, showMessageBox]);


    const designDialogExecuteButtonLink = useCallback(async (dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, linkRequestObj) => {
        if (linkRequestObj.ValidUser !== undefined && !linkRequestObj.ValidUser && linkRequestObj.Code !== undefined) {
            designDialogShowUserLevelWarningDialog(defaultUserLevelWarningTitle, defaultUserLevelWarningMessage);
            return;
        }

        //If this is a real button, call handler directly
        if (linkRequestObj && linkRequestObj.Result) {
            designDialogHandleButtonClick(getTranslatedString, linkRequestObj);
        } else {
            const buttonLink: string = linkRequestObj.Link;

            const buttonArgs = linkRequestObj.Args;

            const templateIdEncoded = encodeUTF8(MemoryStoreLibrary.getString("ciffName"));
            let editorTemplateUrl = `/template/${templateIdEncoded}/content`;

            const subImageId = MemoryStoreLibrary.getString("subImage");
            let actionData;
            const subImageIdEncoded = encodeUTF8(subImageId);

            const buttonPayload = linkRequestObj.Payload;

            switch (buttonLink) {
                case "AddField":
                    //get next Available Field name for the template
                    changeBlockDialogStatus(true);

                    try {
                        const newFieldNameData: any = await runSimpleAjaxCommandAsync({
                            AjaxType: "PostJson",
                            Action: "GetNextAvailableFieldId",
                            AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                            AjaxData: {
                                CiffAction: {
                                    ActionType: "GetNextAvailableFieldId",
                                    ActionPayload: {
                                        SingleActionObject: buttonPayload
                                    }
                                }
                            }
                        });

                        changeBlockDialogStatus(false);
                        const fileNameInputObj = {
                            inputText: newFieldNameData.NewFieldName,
                            editorTemplateUrl,
                            checkFieldExistsUrl: `/template/${templateIdEncoded}/fieldnameavailability`,
                            addFieldUrl: `/template/${templateIdEncoded}/subimages/${subImageIdEncoded}/field/`,
                            templateIdEncoded,
                            subImageIdEncoded,
                            buttonPayload,
                            subImageId
                        };
                        //Show Input screen to get a field name
                        designDialogAddNewField(getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, fileNameInputObj);

                    } catch (xhr: any) {
                        showMessageBox('Error', getTranslatedString("CE_FailedToGetNewFieldName").replace("%1", xhr.status));
                        changeBlockDialogStatus(false);
                    }

                    break;

                case "AddLotCodeField":
                    setSubAddFieldDialogOpened(true);
                    openDialog({
                        initialDataUrl: resolveAbsolutePath(editorTemplateUrl),
                        postData: {
                            CiffName: MemoryStoreLibrary.getString("ciffName"),
                            SubImage: MemoryStoreLibrary.getString("subImage")
                        },
                        controlPageName: "lotcodemanager",
                        firstTimePostData: {},
                        extraHandlerOnSave: null,
                        extraHandlerOnCancel: null,
                        dialogSingleStemActionType: "AddingLotCodeProps",
                        dialogSingleActionUrl: null
                    });

                    break;

                case "AddDateOffset":
                    changeBlockDialogStatus(true);

                    try {
                        const data: any = await runSimpleAjaxCommandAsync({
                            AjaxType: "PutJson",
                            Action: "AddDateOffset",
                            AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                            AjaxData: {
                                CiffAction: {
                                    ActionType: "AddDateOffset",
                                    ActionPayload: {
                                        SingleActionObject: {
                                            SubImage: MemoryStoreLibrary.getString("subImage")
                                        }
                                    }
                                }
                            }
                        });

                        if (data.Error === "ValidationError") {
                            showMessageBox(data.Title, data.Message);
                        } else {
                            showMessageBox(_T("Info"), _T("Date offset added successfully."), true);
                            //Now Update OffsetDate by showing the data offset properties
                            designDialogUpdateOffsetDate(dispatch, getTranslatedString, editorTemplateUrl, templateIdEncoded, data.DateOffsetId);
                        }

                    } catch (xhr: any) {
                        showMessageBox('Error', getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status));
                        changeBlockDialogStatus(false);
                    }

                    break;

                case "EditDateOffset":
                    designDialogUpdateOffsetDate(dispatch, getTranslatedString, editorTemplateUrl, templateIdEncoded, buttonPayload);
                    break;

                case "EditMarkSet":
                    designDialogUpdateMarkSet(dispatch, getTranslatedString, editorTemplateUrl, templateIdEncoded, buttonPayload);
                    break;

                case "AddMarkSet":
                    changeBlockDialogStatus(true);

                    try {
                        const data: any = await runSimpleAjaxCommandAsync({
                            AjaxType: "PutJson",
                            Action: "AddMarkSet",
                            AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                            AjaxData: {
                                CiffAction: {
                                    ActionType: "AddMarkSet",
                                    ActionPayload: {
                                        SingleActionObject: {
                                            SubImage: MemoryStoreLibrary.getString("subImage"),
                                            MarkSetID: buttonPayload
                                        }
                                    }
                                }
                            }
                        });

                        if (data.Error === "ValidationError") {
                            showMessageBox(data.Title, data.Message);
                        } else {
                            showMessageBox(_T("Info"), _T("Mark set added successfully."), true);
                            //Now Update OffsetDate by showing the data offset properties
                            designDialogUpdateMarkSet(dispatch, getTranslatedString, editorTemplateUrl, templateIdEncoded, data.MarkSetId);
                        }

                    } catch (xhr: any) {
                        showMessageBox('Error', getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status));
                        changeBlockDialogStatus(false);
                    }

                    break;

                case "DeleteDateOffset":
                    {
                        const deleteDateOffset = async () => {
                            const postData = {
                                CurrentSubImage: subImageId
                            };
                            const removeDateOffseDeleteData = {
                                FieldsToRemove: JSON.stringify(buttonArgs.FieldsToRemove),
                                SubImage: MemoryStoreLibrary.getString("subImage")
                            };
                            changeBlockDialogStatus(true);

                            actionData = {
                                CiffAction: {
                                    ActionType: "DeleteDateOffset",
                                    ActionPayload: {
                                        SingleActionObject: removeDateOffseDeleteData
                                    }
                                }
                            };

                            try {
                                const data: any = await runSimpleAjaxCommandAsync({
                                    AjaxType: "PutJson",
                                    Action: "DeleteDateOffset",
                                    AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                    AjaxData: actionData
                                });


                                if (data && data.Error === "ValidationError") {
                                    showMessageBox(data.Title, data.Message);
                                } else {
                                    showMessageBox(_T("Info"), _T("date offset deleted successfully."), true);
                                    if (data && data.AffectedFieldNames) {
                                        sendActionToParent({
                                            type: ActionTypes.UP_CiffEditorRefreshAffectedFieldsImageAction,
                                            payload: { AffectedFieldNames: data.AffectedFieldNames }
                                        });
                                    }

                                    const messageData = {
                                        sentData: postData,
                                        recievedData: data,
                                        functionUrl: buttonLink
                                    };
                                    if (buttonArgs && buttonArgs.GenericDialogFunctionDataReceived) {
                                        buttonArgs.GenericDialogFunctionDataReceived(messageData);
                                    }
                                }

                                changeBlockDialogStatus(false);

                            } catch (xhr: any) {
                                const messageData = {
                                    jqXhr: xhr,
                                    functionUrl: buttonLink
                                };


                                if (buttonArgs && buttonArgs.GenericDialogFunctionCallFailed) {

                                    const errorInfo = JSON.parse(messageData.jqXhr.statusText);
                                    buttonArgs.GenericDialogFunctionCallFailed(errorInfo?.message);
                                }

                                changeBlockDialogStatus(false);
                            }
                        };

                        sendActionToParent({
                            type: ActionTypes.EXECUTE_ShowDecisionDialogAction,
                            payload: {
                                alertTitle: _T("Confirm"),
                                alertMessage: _T("Are you sure want to delete date offset?"),
                                alertIcon: "fa-exclamation-circle",
                                iconColor: "danger",
                                thenFunc: (buttonResult) => {
                                    if (buttonResult === DesignConstants.decisionDialogYesResult) {
                                        deleteDateOffset();
                                    }
                                }
                            }
                        });
                    }

                    break;

                case "DeleteMarkSet":
                    {

                        const deleteMarkSet = async () => {
                            const postData = {
                                CurrentSubImage: subImageId
                            };
                            const removeMarkSetDeleteData = {
                                FieldsToRemove: JSON.stringify(buttonArgs.FieldsToRemove),
                                SubImage: MemoryStoreLibrary.getString("subImage")
                            };
                            changeBlockDialogStatus(true);

                            actionData = {
                                CiffAction: {
                                    ActionType: "DeleteMarkSet",
                                    ActionPayload: {
                                        SingleActionObject: removeMarkSetDeleteData
                                    }
                                }
                            };

                            try {
                                const data: any = await runSimpleAjaxCommandAsync({
                                    AjaxType: "PutJson",
                                    Action: "DeleteMarkSet",
                                    AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                    AjaxData: actionData
                                });

                                if (data && data.Error === "ValidationError") {
                                    showMessageBox(data.Title, data.Message);
                                } else {
                                    showMessageBox(_T("Info"), _T("Mark set deleted successfully."), true);
                                    if (data && data.AffectedFieldNames) {
                                        sendActionToParent({
                                            type: ActionTypes.UP_CiffEditorRefreshAffectedFieldsImageAction,
                                            payload: { AffectedFieldNames: data.AffectedFieldNames }
                                        });
                                    }

                                    const messageData = {
                                        sentData: postData,
                                        recievedData: data,
                                        functionUrl: buttonLink
                                    };
                                    if (buttonArgs && buttonArgs.GenericDialogFunctionDataReceived) {
                                        buttonArgs.GenericDialogFunctionDataReceived(messageData);
                                    }
                                }

                                changeBlockDialogStatus(false);

                            } catch (xhr: any) {
                                const messageData = {
                                    jqXhr: xhr,
                                    functionUrl: buttonLink
                                };

                                if (buttonArgs && buttonArgs.GenericDialogFunctionCallFailed) {
                                    buttonArgs.GenericDialogFunctionCallFailed(messageData);
                                }
                                changeBlockDialogStatus(false);
                            }
                        };

                        sendActionToParent({
                            type: ActionTypes.EXECUTE_ShowDecisionDialogAction,
                            payload: {
                                alertTitle: _T("Confirm"),
                                alertMessage: _T("Are you sure want to delete mark set?"),
                                alertIcon: "fa-exclamation-circle",
                                iconColor: "danger",
                                thenFunc: (buttonResult) => {
                                    if (buttonResult === DesignConstants.decisionDialogYesResult) {
                                        deleteMarkSet();
                                    }
                                }
                            }
                        });
                    }

                    break;

                case "RefreshFieldType":
                    changeBlockDialogStatus(true);

                    if (buttonPayload && (buttonPayload.PageName === "UserDate" || buttonPayload.PageName === "OffsetDate")) {
                        setSubAddFieldDialogOpened(true);
                        setDialogName("genericDialog");
                    }

                    try {
                        const data: any = await runSimpleAjaxCommandAsync({
                            AjaxType: "PostJson",
                            Action: "GetFieldType",
                            AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                            AjaxData: {
                                CiffAction: {
                                    ActionType: "GetFieldType",
                                    ActionPayload: {
                                        SingleActionObject: {
                                            PageName: linkRequestObj.Payload.PageName,
                                            SubImage: subImageId
                                        }
                                    }
                                }
                            }
                        });
                        changeBlockDialogStatus(false);
                        designDialogProcessComponentsData(data);

                    } catch (xhr: any) {
                        showMessageBox('Error', getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status));
                        changeBlockDialogStatus(false);
                    }

                    break;

                case "AddSubImage":
                    {
                        const postData = {
                            CurrentSubImage: subImageId
                        };

                        actionData = {
                            CiffAction: {
                                ActionType: "AddSubImage",
                                ActionPayload: {
                                    SingleActionObject: postData
                                }
                            }
                        };


                        changeBlockDialogStatus(true);

                        try {
                            const data: any = await runSimpleAjaxCommandAsync({
                                AjaxType: "PutJson",
                                Action: "AddSubImage",
                                AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                AjaxData: actionData
                            });
                            if (data.Error === "ValidationError") {
                                showMessageBox(data.Title, data.Message);
                            } else {
                                showMessageBox(_T("Info"), _T("Sub image added successfully."), true);
                                const messageData = {
                                    sentData: postData,
                                    recievedData: data,
                                    functionUrl: buttonLink
                                };
                                if (buttonArgs && buttonArgs.GenericDialogFunctionDataReceived) {
                                    buttonArgs.GenericDialogFunctionDataReceived(messageData);
                                }

                            }
                            changeBlockDialogStatus(false);

                        } catch (xhr: any) {
                            const messageData = {
                                jqXhr: xhr,
                                functionUrl: buttonLink
                            };

                            const exception = extractExceptionName(messageData.jqXhr.response?.data.ErrorMessage);

                            if (buttonArgs && buttonArgs.GenericDialogFunctionCallFailed) {
                                if (exception === "CiffTooManySubImagesException") {
                                    buttonArgs.GenericDialogFunctionCallFailed(getTranslatedString("CE_TooManySubimages"));
                                } else {
                                    const errorInfo = JSON.parse(messageData.jqXhr.statusText);
                                    buttonArgs.GenericDialogFunctionCallFailed(errorInfo?.message);
                                }
                            }
                            changeBlockDialogStatus(false);
                        }
                    }

                    break;

                case "DeleteSubImage":
                    {

                        const deleteSubImage = async () => {
                            const postData = {
                                CurrentSubImage: subImageId
                            };

                            const deleteData = {
                                FieldsToRemove: JSON.stringify(buttonArgs.FieldsToRemove),
                                CurrentSubImage: subImageId
                            };

                            actionData = {
                                CiffAction: {
                                    ActionType: "DeleteSubImage",
                                    ActionPayload: {
                                        SingleActionObject: deleteData
                                    }
                                }
                            };


                            changeBlockDialogStatus(true);

                            try {
                                const data: any = await runSimpleAjaxCommandAsync({
                                    AjaxType: "PutJson",
                                    Action: "DeleteSubImage",
                                    AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                    AjaxData: actionData
                                });
                                if (data.Error === "ValidationError") {
                                    showMessageBox(data.Title, data.Message);
                                } else {
                                    showMessageBox(_T("Info"), _T("Sub image deleted successfully."), true);
                                    const messageData = {
                                        sentData: postData,
                                        recievedData: data,
                                        functionUrl: buttonLink
                                    };
                                    if (buttonArgs && buttonArgs.GenericDialogFunctionDataReceived) {
                                        buttonArgs.GenericDialogFunctionDataReceived(messageData);
                                    }

                                }
                                changeBlockDialogStatus(false);

                            } catch (xhr: any) {
                                const messageData = {
                                    jqXhr: xhr,
                                    functionUrl: buttonLink
                                }

                                const exception = extractExceptionName(messageData.jqXhr.response?.data.ErrorMessage);

                                if (buttonArgs && buttonArgs.GenericDialogFunctionCallFailed) {
                                    if (exception === "CiffRemoveFirstSubimageException") {
                                        buttonArgs.GenericDialogFunctionCallFailed(getTranslatedString("CE_RemoveFirstSubimage"));
                                    } else if (exception === "CiffRemoveCurrentSubimageException") {
                                        buttonArgs.GenericDialogFunctionCallFailed(getTranslatedString("CE_RemoveCurrentSubimage"));
                                    } else {
                                        const errorInfo = JSON.parse(messageData.jqXhr.statusText);
                                        buttonArgs.GenericDialogFunctionCallFailed(errorInfo?.message);
                                    }
                                }

                                changeBlockDialogStatus(false);
                            }
                        }

                        sendActionToParent({
                            type: ActionTypes.EXECUTE_ShowDecisionDialogAction,
                            payload: {
                                alertTitle: _T("Confirm"),
                                alertMessage: _T("Are you sure want to delete sub image?"),
                                alertIcon: "fa-exclamation-circle",
                                iconColor: "danger",
                                thenFunc: (buttonResult) => {
                                    if (buttonResult === DesignConstants.decisionDialogYesResult) {
                                        deleteSubImage();
                                    }
                                }
                            }
                        });
                    }

                    break;

                case "AddNewNamedParameter":
                    {
                        const namedParameterData = designDialogCollectDialogData();
                        const updateDataUrl = `/template/${templateIdEncoded}/content`;
                        const updateDataAction = "UpdateNamedParamsProps";

                        const updateData = mergeObjects(namedParameterData, { "UpdateType": "Save", SubImage: MemoryStoreLibrary.getString("subImage") });


                        try {

                            const data: any = await runSimpleAjaxCommandAsync({
                                AjaxType: "PutJson",
                                Action: updateDataAction,
                                AjaxUrl: updateDataUrl,
                                AjaxData: {
                                    CiffAction: {
                                        ActionType: updateDataAction,
                                        ActionPayload: {
                                            SingleActionObject: updateData
                                        }
                                    }
                                }
                            });

                            if (data.Error) {
                                showMessageBox('Error', data.Message);
                            }

                        } catch (xhr: any) {
                            designDialogCloseDialog();
                            showMessageBox('Error', getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status));
                            changeBlockDialogStatus(false);
                        }

                        stackedDialogs.current.push({
                            DialogUrl: resolveAbsolutePath(editorTemplateUrl),
                            DialogControlName: "generic",
                            PostData: {
                                CiffAction: {
                                    ActionType: "GetNamedParamsProps",
                                    ActionPayload: {
                                        SingleActionObject: {
                                            CiffName: MemoryStoreLibrary.getString("ciffName"),
                                            SubImage: MemoryStoreLibrary.getString("subImage")
                                        }
                                    }
                                }
                            },
                            DialogSingleStemActionType: "NamedParamsProps",
                        });

                        changeBlockDialogStatus(true);

                        try {
                            const data: any = await runSimpleAjaxCommandAsync({
                                AjaxType: "PostJson",
                                Action: "GetAddingNamedParamsProps",
                                AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                AjaxData: {
                                    CiffAction: {
                                        ActionType: "GetAddingNamedParamsProps",
                                        ActionPayload: {
                                            SingleActionObject: {
                                                SubImage: MemoryStoreLibrary.getString("subImage")
                                            }
                                        }
                                    }
                                }
                            });
                            if (data.Error) {
                                showMessageBox('Error', data.Message);
                            } else {
                                sendActionToParent({ type: ActionTypes.UP_FileIconEnableAction, payload: "fileIcon" });
                                data.SingleStemActionType = "AddingNamedParamsProps";
                                setBaseUrl(editorTemplateUrl);
                                designDialogProcessComponentsData(data);
                                changeBlockDialogStatus(false);
                            }

                        } catch (xhr: any) {
                            designDialogCloseDialog();
                            showMessageBox('Error', getTranslatedString("CE_SaveErrorWithCode").replace("%1", xhr.status));
                            changeBlockDialogStatus(false);
                        }
                    }

                    break;
                case "DeleteNamedParam":
                    {
                        const deleteNamedParam = async () => {
                            changeBlockDialogStatus(true);
                            try {
                                const data: any = await runSimpleAjaxCommandAsync({
                                    AjaxType: "PutJson",
                                    Action: "DeleteNamedParams",
                                    AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                    AjaxData: {
                                        CiffAction: {
                                            ActionType: "DeleteNamedParams",
                                            ActionPayload: {
                                                SingleActionObject: {
                                                    NamedPropertyToDelete: linkRequestObj.PropertyName,
                                                    SubImage: subImageId
                                                }
                                            }
                                        }
                                    }
                                });
                                sendActionToParent({ type: ActionTypes.UP_FileIconEnableAction, payload: "fileIcon" });
                                designDialogProcessComponentsData(data);
                                changeBlockDialogStatus(false);
                                showMessageBox(_T("Info"), _T("Parameter deleted successfully."), true);

                            } catch (xhr: any) {
                                showMessageBox('Error', xhr.status ? getTranslatedString("CE_DeleteNamedParamError").replace("%1", xhr.status.toString()) : xhr.toString());
                                changeBlockDialogStatus(false);
                            }
                        };

                        sendActionToParent({
                            type: ActionTypes.EXECUTE_ShowDecisionDialogAction,
                            payload: {
                                alertTitle: _T("Confirm"),
                                alertMessage: _T("Are you sure want to delete parameter?"),
                                alertIcon: "fa-exclamation-circle",
                                iconColor: "danger",
                                thenFunc: (buttonResult) => {
                                    if (buttonResult === DesignConstants.decisionDialogYesResult) {
                                        deleteNamedParam();
                                    }
                                }
                            }
                        });
                    }

                    break;
                case "AddTemplateComment":
                    changeBlockDialogStatus(true);
                    editorTemplateUrl = `/template/${templateIdEncoded}/comments`;
                    try {
                        const data: any = await runSimpleAjaxCommandAsync({
                            AjaxType: "PutJson",
                            Action: "CommentsProps",
                            AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                            AjaxData: {
                                PropertyName: linkRequestObj.PropertyName,
                                PropertyValue: linkRequestObj.PropertyValue
                            }
                        });
                        designDialogProcessComponentsData(data);
                        changeBlockDialogStatus(false);
                        showMessageBox(_T("Info"), _T("Template comment added successfully."), true);
                    } catch (xhr: any) {
                        showMessageBox('Error', xhr.status ? getTranslatedString("CE_NewCommentSaveError").replace("%1", xhr.status.toString()) : xhr.toString());
                        changeBlockDialogStatus(false);
                    }

                    break;
            }

            // Allows other controls to ask this dialog to execute a command link

            // create a dummy control definition, all we need for the link parser is "result" and "link" to be present
            // but we need the definition passed to the method to match that of a full controls interface type hence the mostly empty structure
            const myControl = new PageComponentData("dummyButton",
                {
                    Title: "",
                    Image: "",
                    Link: buttonLink,
                    Result: { key: "", value: "" },
                    ShowText: false,
                    Tran: "",
                    Type: DesignConstants.customButton,
                    Pattern: "",
                    ReadOnly: false,
                    Refresh: 0,
                    Redraw: 0,
                    Page: 0,
                    Value: "",
                    Values: null,
                    Dp: 0,
                    Min: 0,
                    Max: 0,
                    Step: 0,
                    Units: "",
                    ComponentName: "dummyButton",
                    ComponentType: "customButton",
                    AddUrl: "",
                    CopyUrl: "",
                    EditUrl: "",
                    RemoveUrl: "",
                    Parent: null,
                    number: 0
                });

            designDialogButtonLinkResolver(myControl);

            // Once we've parsed the command language in the link, we then need to make the generic dialog think
            // that the button was clicked, by passing the button click data directly to the button handler.
            let opensDialog = false;
            if (myControl["OpensDialog"]) {
                opensDialog = myControl["OpensDialog"];
            }

            let callsFunction = false;
            if (myControl["CallsFunction"]) {
                callsFunction = myControl["CallsFunction"];
            }

            const dummyButtonData: any = {
                Parent: myControl.Parent,
                Link: myControl.Link,
                Result: myControl.Result,
                PageName: myControl.Result && myControl.Result["PageName"] ? myControl.Result["PageName"] : "",
                OpensDialog: opensDialog,
                CallsFunction: callsFunction,
                HandlingType: myControl["HandlingType"]
            };

            if (linkRequestObj["GenericDialogFunctionDataReceived"]) {
                dummyButtonData.GenericDialogFunctionDataReceived = linkRequestObj.GenericDialogFunctionDataReceived;
            }

            if (linkRequestObj["GenericDialogFunctionCallFailed"]) {
                dummyButtonData.GenericDialogFunctionCallFailed = linkRequestObj.GenericDialogFunctionCallFailed;
            }

            designDialogHandleButtonClick(getTranslatedString, dummyButtonData);
        }
    }, [sendActionToParent, designDialogShowUserLevelWarningDialog, defaultUserLevelWarningTitle, defaultUserLevelWarningMessage, designDialogHandleButtonClick, designDialogButtonLinkResolver, changeBlockDialogStatus, openDialog, designDialogUpdateOffsetDate, designDialogUpdateMarkSet, runSimpleAjaxCommandAsync, designDialogAddNewField, showMessageBox, sendActionToParent, designDialogProcessComponentsData, extractExceptionName, designDialogCollectDialogData, designDialogCloseDialog]);

    const designDialogCloseButtonHandler = useCallback((dispatch, getTranslatedString) => {
        if (extraHandlerOnCancel) {
            extraHandlerOnCancel({
                dialogStackLength: stackedDialogs.current.length
            });
        }

        if (closeButtonDefinition !== null) {
            if (closeButtonDefinition.Link) {

                let opensDialog = false;
                if (closeButtonDefinition["OpensDialog"]) {
                    opensDialog = closeButtonDefinition.OpensDialog;
                }

                const closeButtonData = {
                    Parent: closeButtonDefinition.Parent,
                    Link: resolveAbsolutePath(closeButtonDefinition.Link),
                    Result: closeButtonDefinition.Result,
                    PageName: closeButtonDefinition.Name,
                    OpensDialog: opensDialog
                };

                designDialogHandleButtonClick(getTranslatedString, closeButtonData);
            } else {
                if (stackedDialogs.current.length === 0) {
                    setOverlayWithDelayInvoker(dispatch, false);
                    designDialogCloseDialog();
                } else {
                    designDialogRefreshDialogWithStackedDialogWithTheSameDialogType(getTranslatedString);
                }
            }
        } else {
            designDialogCloseDialog();
        }
    }, [extraHandlerOnCancel, closeButtonDefinition, stackedDialogs, designDialogHandleButtonClick, designDialogCloseDialog, designDialogRefreshDialogWithStackedDialogWithTheSameDialogType]);


    const designDialogDialogTabBarIconClicked = useCallback((getTranslatedString, clickData) => {
        // construct a button click data based on tab click
        const buttonData = {
            Link: baseUrl,
            PageName: clickData.ButtonName,
            Parent: null,
            Result: {}
        };

        // Then let the regular button code deal with it
        designDialogHandleButtonClick(getTranslatedString, buttonData, DesignConstants.linkSuffixRefresh);
    }, [baseUrl, designDialogHandleButtonClick]);



    useEffect(() => {
        if (dialogOpenIndex === 1) {
            setDialogOpenIndex(2);
            setSelectedTabIndexInvoker(dispatch, 0);
            designDialogLoadInitialDialogData();
        }
    }, [dialogOpenIndex, dispatch, designDialogLoadInitialDialogData]);

    useEffect(() => {
        if (dialogOpenIndex === 2) {
            setDialogOpenIndex(3);
            setIsDialogOpen(true);
            setIsDialogVisible(true);
        }
    }, [dialogOpenIndex, dispatch, designDialogLoadInitialDialogData]);


    useEffect(() => {
        (async () => {
            if (isDialogOpen && dialogName === "mergefieldmanager") {
                if (!isDialogClosing) {
                    if (isDialogInError && !doesDialogHaveError) {
                        setIsDialogInError(false);
                        const templateIdEncoded = encodeUTF8(MemoryStoreLibrary.getString('ciffName'));
                        const subImageId = MemoryStoreLibrary.getString('subImage');

                        const editMergeFieldData = collectMergeFieldDataToSendToServer(false);
                        editMergeFieldData.SubImage = subImageId;
                        editMergeFieldData.FieldName = fieldNameShadowCopy;

                        changeBlockDialogStatus(true);

                        const editorTemplateUrl = `/template/${templateIdEncoded}/content`;

                        try {
                            await runSimpleAjaxCommandAsync({
                                AjaxType: 'PutJson',
                                Action: 'SaveMerge',
                                AjaxUrl: resolveAbsolutePath(editorTemplateUrl),
                                AjaxData: {
                                    CiffAction: {
                                        ActionType: 'UpdateMergeProps',
                                        ActionPayload: {
                                            SingleActionObject: editMergeFieldData,
                                        },
                                    },
                                }
                            });
                            designDialogPreviewDialog();

                        } catch (xhr: any) {
                            designDialogCloseDialog();
                            designDialogShowErrorBox(
                                'Error',
                                xhr.status ? getTranslatedString('CE_SaveErrorWithCode').replace('%1', xhr.status.toString()) : xhr.toString()
                            );
                            changeBlockDialogStatus(false);
                        }

                    }
                }
                if (doesDialogHaveError) {
                    setIsDialogInError(true);
                }
            }
        })();

    }, [doesDialogHaveError, dialogName, changeBlockDialogStatus, sendActionToParent, getTranslatedString, isDialogOpen, isDialogClosing, isDialogInError, collectMergeFieldDataToSendToServer, fieldNameShadowCopy, resolveTheSameLevelActions, designDialogPreviewDialog, designDialogCloseDialog, designDialogShowErrorBox, runSimpleAjaxCommandAsync]);


    useEffect(() => {
        setFieldNameShadowCopy(everyRequestPostData.FieldName);
    }, [everyRequestPostData.FieldName, everyRequestPostData.CiffName])

    const anyErrorExists = useMemo(() => {
        let anyErrorFlag = false;
        for (const key in controlErrorFlags) {
            if (controlErrorFlags[key] === DesignConstants.controlStateInvalid) {
                anyErrorFlag = true;
            }
        }

        return doesDialogHaveError || anyErrorFlag;
    }, [doesDialogHaveError, controlErrorFlags]);

    const handleOkClick = useCallback(() => {
        if (okButtonDefinition !== null) {
            let opensDialog;
            let okButtonData;

            switch (okButtonDefinition.Action) {
                case "CMD_CLOSE":
                    designDialogCloseDialog();
                    break;
                case "CMD_SAVE":
                    {
                        opensDialog = false;
                        if (okButtonDefinition["OpensDialog"]) {
                            opensDialog = okButtonDefinition.OpensDialog;
                        }

                        let buttonResult = mergeObjects(
                            okButtonDefinition.Result,

                            { PageName: okButtonDefinition.Page }
                        );

                        if (okButtonDefinition.Payload) {
                            buttonResult = mergeObjects(buttonResult, okButtonDefinition.Payload);
                        }

                        okButtonData = {
                            Parent: okButtonDefinition.Parent,
                            Link: okButtonDefinition.Action,
                            Result: buttonResult,
                            PageName: okButtonDefinition.Page,
                            OpensDialog: opensDialog
                        };

                        designDialogHandleButtonClick(getTranslatedString, okButtonData, DesignConstants.linkSuffixSave);
                    }
                    break;
                case "AddField":
                    {
                        //Add field is a simple version of a wizard page's last action
                        //Collect current data
                        let wizardData = designDialogCollectDialogData();
                        wizardData = mergeObjects(wizardData, { PageName: okButtonDefinition.Page });

                        if (okButtonDefinition.Payload) {
                            wizardData = mergeObjects(wizardData, okButtonDefinition.Payload);
                        }

                        const fullWizardData = {
                            Parent: okButtonDefinition.Parent,
                            Link: okButtonDefinition.Action,
                            Payload: wizardData,
                            PageName: okButtonDefinition.Page
                        };

                        designDialogExecuteButtonLink(dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, fullWizardData);
                    }
                    break;
                case "CreatePackagingCheck":
                    {
                        const packCheckButton = {
                            // Parent: okButtonDefinition.Parent,
                            Link: okButtonDefinition.Action,
                            Payload: designDialogCollectDialogData(),
                            PageName: okButtonDefinition.Page
                        };

                        designDialogExecuteButtonLink(dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, packCheckButton);
                    }
                    break;
                case "CreateLotCodeField":
                    designDialogExecuteButtonLink(dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, {
                        // Parent: okButtonDefinition.Parent,
                        Link: okButtonDefinition.Action,
                        Payload: designDialogCollectDialogData(),
                        PageName: okButtonDefinition.Page
                    });
                    break;
            }

        } else {
            designDialogCloseDialog();
        }

        setSelectedTabBarIconIndex(0);
    }, [okButtonDefinition, designDialogCloseDialog, designDialogExecuteButtonLink, dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, designDialogCollectDialogData, designDialogHandleButtonClick]);

    const handleCloseClick = useCallback(() => {
        if (pageRefreshed && okButtonDefinition !== null) {
            handleOkClick();
            setPageRefreshed(false);
        } else {

            if (dialogName !== 'addfielddialog') {
                setSubAddFieldDialogOpened(false);
                setPreviewImageUrl("");
                if (subAddFieldDialogOpened) {
                    // Close the subAddFieldDialog
                    setDialogName("addfielddialog");
                    const returnBack = {
                        Parent: null,
                        Link: "RefreshFieldType",
                        Payload: {
                            PageName: "Field"
                        },
                    };

                    designDialogExecuteButtonLink(dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, returnBack);

                    return;
                }

                setIsDialogClosing(true);
                setDialogSingleStemActionType("");
                closeDialog();

                dispatch({
                    type: ActionTypes.STORE_UpdateDoesDialogHaveErrorAction,
                    payload: false
                });

                setParsedDialogData(s => ({ ...s, nonComponentData: {} }));

            } else {
                designDialogCloseButtonHandler(dispatch, getTranslatedString);
            }
        }

        setIsDialogOpen(false);
        setBaseUrl('');
        setSelectedTabBarIconIndex(0);
    }, [dialogName, subAddFieldDialogOpened, okButtonDefinition, pageRefreshed, handleOkClick, closeDialog, dispatch, designDialogExecuteButtonLink, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, designDialogCloseButtonHandler]);

    useEffect(() => {
        if (actionFromParent && actionFromParent !== previousActionFromParent) {
            setPreviousActionFromParent(actionFromParent);
            switch (actionFromParent.type) {
                case ActionTypes.DOWN_OpenDialogAction:
                    setSubAddFieldDialogOpened(false);
                    openDialog(actionFromParent.payload);
                    break;

                case ActionTypes.UP_ExecuteButtonLinkAction:
                    designDialogExecuteButtonLink(dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, actionFromParent.payload);
                    break;
                case ActionTypes.UP_PageControlValidationStateAction:
                    designDialogPageControlValidationState(actionFromParent.payload);
                    break;
            }
        }
    }, [actionFromParent, previousActionFromParent, openDialog, designDialogExecuteButtonLink, dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, designDialogPageControlValidationState]);

    useEffect(() => {
        if (initateDisplayComponents && isDialogOpen) {
            setInitateDisplayComponents(false);

            setIsDialogOpen(true);

            designDialogRenderTab();
            setIsDialogVisible(true);
            if (previewAvailableInDialog) {
                setPreviewWorkflowIndex(1);
            }
        }
    }, [designDialogRenderTab, initateDisplayComponents, isDialogOpen, previewAvailableInDialog]);

    const onSendActionToParentFromControls = useCallback((action: any) => {
        if (action && action.type) {
            switch (action.type) {
                case ActionTypes.EXECUTE_ShowAlertBoxAction:
                case ActionTypes.EXECUTE_ShowInputBoxAction:
                case ActionTypes.EXECUTE_ShowDecisionDialogAction:
                case ActionTypes.UP_FileIconEnableAction:
                    resolveTheSameLevelActions(action);
                    break;

                case ActionTypes.UP_RefreshPaneAction:
                    designDialogRefreshPage(dispatch, getTranslatedString, action.payload);
                    break;

                case ActionTypes.UP_ExecuteButtonLinkAction:
                    designDialogExecuteButtonLink(dispatch, getTranslatedString, initialAddFieldXPos, initialAddFieldYPos, action.payload);
                    break;
                case ActionTypes.UP_ExecuteSimpleButtonLinkAction:
                case ActionTypes.UP_Block_Item_Removed:
                    executeButtonLink(action.payload);
                    break;

                case ActionTypes.UP_DialogTabBarIconClickedAction:
                    designDialogDialogTabBarIconClicked(getTranslatedString, action.payload);
                    break;

                case ActionTypes.UP_PageControlValidationStateAction:
                    designDialogPageControlValidationState(action.payload);
                    break;

                case ActionTypes.UP_UpdateIsBlockedAction:
                case ActionTypes.UP_InitiateReOpenDialogAction:
                    resolveTheSameLevelActions(action);
                    break;

                case ActionTypes.UP_ConstantListBoxItemSelectedWithoutBtnAction:
                    if (dialogName === 'compositedatabuildermanager') {
                        getNewAiProperties(action.payload);
                    }
                    sendActionToChildren({ type: 'DOWN_ConstantListBoxDeselectItem' });
                    break;
                case ActionTypes.UP_Block_Item_Selected:
                    getExistingAiProperties(action.payload);
                    //  $('#control_items_availableaiblocks').val('');
                    break;
                case ActionTypes.UP_Block_Item_UnSelected:
                    resolveTheSameLevelActions({ type: ActionTypes.UP_Reset_Ai_Properties_Form });
                    break;
                case ActionTypes.UP_Get_Ai_Block_Properties:
                    getNewAiProperties(action.payload);
                    break;
                case ActionTypes.UP_Valid_Ai_User_Data_Entered:
                    sendActionToChildren({ type: ActionTypes.UP_ConstantListBoxItemSelectedAction, payload: action.payload });
                    break;
                case ActionTypes.UP_BlockList_Items_Reorderd:
                    setAiBlocksReordered(action.payload);
                    resolveTheSameLevelActions({ type: ActionTypes.UP_Reset_Ai_Properties_Form });
                    break;

                case ActionTypes.UP_CheckboxStatusChangedAction:
                    {
                        const userEditable = action.payload.PropertyName.indexOf('TextUserEditable') != -1 ? action.payload.PropertyValue : isUserEditable;
                        const fixedLength = action.payload.PropertyName.indexOf('FixedLength') != -1 ? action.payload.PropertyValue : isFixedLength;

                        if (action.payload.PropertyName.indexOf('TextUserEditable') != -1) {
                            setIsUserEditable(userEditable);
                            sendActionToChildren({
                                type: ActionTypes.DOWN_ShowHideChildControl,
                                payload: {
                                    visibleState: userEditable,
                                    tags: ['UserEditableField']
                                }
                            })
                            sendActionToChildren({
                                type: ActionTypes.DOWN_ShowHideChildControl,
                                payload: {
                                    visibleState: userEditable && fixedLength,
                                    tags: ['FixedLengthField']
                                }
                            })
                        }
                        if (action.payload.PropertyName.indexOf('FixedLength') != -1) {
                            setIsFixedLength(fixedLength);
                            sendActionToChildren({
                                type: ActionTypes.DOWN_ShowHideChildControl,
                                payload: {
                                    visibleState: userEditable && fixedLength,
                                    tags: ['FixedLengthField']
                                }
                            })
                        }
                    }
                    break;
                case ActionTypes.UP_UpdateLotCodeFieldsVisibility:
                    sendActionToChildren({
                        type: ActionTypes.DOWN_ShowHideChildControl,
                        payload: {
                            visibleState: isUserEditable,
                            tags: ['UserEditableField']
                        }
                    })
                    sendActionToChildren({
                        type: ActionTypes.DOWN_ShowHideChildControl,
                        payload: {
                            visibleState: isUserEditable && isFixedLength,
                            tags: ['FixedLengthField']
                        }
                    })
                    break;
                default:
                    //If there is no handle for the action type, we will send it to the children for consideration (as property update)
                    //Therefore generic dialog will play switchboard role to distribute upcoming actions to children
                    //Therefore siblingt components can communicate via this idea
                    sendActionToChildren(action);
                    break;
            }
        }
    }, [
        resolveTheSameLevelActions,
        designDialogRefreshPage,
        dispatch,
        getTranslatedString,
        designDialogExecuteButtonLink,
        initialAddFieldXPos,
        initialAddFieldYPos,
        executeButtonLink,
        designDialogDialogTabBarIconClicked,
        designDialogPageControlValidationState,
        dialogName,
        sendActionToChildren,
        getExistingAiProperties,
        getNewAiProperties,
        isUserEditable,
        isFixedLength,
    ]);


    useEffect(() => {
        if (previewWorkflowIndex === 1 && isDialogOpen) {
            setTimeout(() => {
                setPreviewWorkflowIndex(2);
            }, 500);
        }
    }, [isDialogOpen, previewWorkflowIndex])

    useEffect(() => {
        if (previewWorkflowIndex === 2) {
            previewDialog();
            setPreviewWorkflowIndex(0);
        }
    }, [previewDialog, previewWorkflowIndex]);

    useEffect(() => {
        return (() => {
            setPreviewImageUrl("");
            setTabBarVisible(false);
            setSelectedTabBarIconIndex(0);
        });
    }, []);


    useEffect(() => {
        if (previewWorkflowIndex === 1 && isDialogOpen) {
            setTimeout(() => {
                setPreviewWorkflowIndex(2);
            }, 500);
        }
    }, [isDialogOpen, previewWorkflowIndex])

    useEffect(() => {
        if (previewWorkflowIndex === 2) {
            previewDialog();
            setPreviewWorkflowIndex(0);
        }
    }, [previewDialog, previewWorkflowIndex]);

    useEffect(() => {
        return (() => {
            setPreviewImageUrl("");
            setTabBarVisible(false);
            setSelectedTabBarIconIndex(0);
        });
    }, []);


    return isDialogOpen ?
        <DialogOverlay>
            {!isDialogVisible && (
                <LoadSpinner fontAwesomeSize={4} extraClasses="text-primary" />
            )}

            <StyledDialogWithBackdropClick
                disableBackdropClick
                fullWidth={true}
                maxWidth={maxWidth || "xs"}
                disableEscapeKeyDown
                aria-labelledby="alert"
                open={true}
            >
                <StyledDialogTitle id="alert-dialog-title">
                    {_T(pageTitle)}
                </StyledDialogTitle>
                <DialogContent dividers>
                    <MainDialogContainer>
                        {tabBarVisible && (
                            <TabBar>
                                <DialogTabBarContainer className="leftbardialog">
                                    <div className="tabbarHolder">
                                        <DialogTabBarField {...dialogTabBarProps}
                                            mapStateToProps={dialogStateToPropsObservable.current}
                                            sendActionToParent={onSendActionToParentFromControls}
                                            setSelectedTabBarIconIndex={setSelectedTabBarIconIndex}
                                            selectedTabBarIconIndex={selectedTabBarIconIndex}
                                        />
                                    </div>
                                </DialogTabBarContainer>
                            </TabBar>
                        )}
                        <div style={{ padding: 10, width: '100%' }}>
                            {previewImageUrl && (
                                <Stack spacing={2} direction={'row'} alignItems={'center'} justifyContent={'center'} sx={{ marginBottom: '5px' }}>
                                    <PreviewImgContainer sx={{ display: 'flex', justifyContent: 'center', width: '100%', border: '1px solid gray', borderRadius: '5px' }}>
                                        <img src={previewImageUrl} alt="Preview" />
                                    </PreviewImgContainer>

                                    {previewAvailableInDialog &&
                                        <Box>
                                            <IconButton
                                                onClick={previewDialog}
                                                disabled={false}
                                                color='primary'
                                            >
                                                <Tooltip title={_T("Refresh")}><RefreshIcon data-testid="RefreshIcon" /></Tooltip>
                                            </IconButton>
                                        </Box>
                                    }
                                </Stack>
                            )}

                            {parsedDialogData.displayableControls && parsedDialogData.displayableControls.map(el => ({
                                ...el,
                                sendActionToParent: onSendActionToParentFromControls,
                                PropertyCollectionDictionary: PropertyCollectionDictionary,
                                UpdatePropertyCollectionDictionary: updatePropertyCollectionDictionary,
                                refreshIndex,
                                CiffName: MemoryStoreLibrary.getString("ciffName"),
                                SubImage: MemoryStoreLibrary.getString("subImage"),
                                mapStateToProps: dialogStateToPropsObservable.current,
                                extraRightMargin: !mergeFieldDialog && (previewAvailableInDialog || parsedDialogData.displayableControls.filter((f: any) => f.ComponentType === 'calendarrulessmartlistcomponent').length > 0) ? '45px' : undefined,
                                dialogName
                            })).map((comp: any, compIndex: number) => (
                                <span key={`${comp.PropertyName}-${compIndex}`}>
                                    {/* {comp.ComponentType} */}
                                    {comp.ComponentType === 'databuilderForm' && <CompositeDataBuilderFormField {...comp} />}
                                    {comp.ComponentType === 'nodisplay' && <NoDisplayField {...comp} />}
                                    {comp.ComponentType === 'blocklist' && <BlockListField {...comp} />}
                                    {comp.ComponentType === 'toggleblocklist' && <ToggleBlockListField {...comp} />}
                                    {comp.ComponentType === 'custombutton' && <CustomButtonField {...comp} />}
                                    {comp.ComponentType === 'checkbox' && <CheckBoxField {...comp} />}
                                    {comp.ComponentType === 'combobox' && <ComboBoxField {...comp} />}
                                    {comp.ComponentType === 'numberbox' && <TextBoxField {...comp} isNumericType={true} />}
                                    {comp.ComponentType === 'textbox' && <TextBoxField {...comp} />}
                                    {comp.ComponentType === 'blocktextbox' && <BlockTextBoxField {...comp} />}
                                    {comp.ComponentType === 'expandablecombobox' && <ExpandableComboBoxField {...comp} />}
                                    {comp.ComponentType === 'constanttextbox' && <ConstantTextBoxField {...comp} />}
                                    {comp.ComponentType === 'constantlistbox' && <ConstantListBoxField {...comp} />}
                                    {comp.ComponentType === 'customlabel' && <LabelField {...comp} />}
                                    {comp.ComponentType === 'smartlistcomponent' && <SmartListField {...comp} />}
                                    {comp.ComponentType === 'smartlistrow' && <SmartListRowField {...comp} />}
                                    {comp.ComponentType === 'tabpageicon' && <TabPageIconField {...comp} />}
                                    {comp.ComponentType === 'aipropertiescomponent' && <CompositeDataBuilderFormField {...comp} />}
                                    {comp.ComponentType === 'calendarrulessmartlistcomponent' && <CalendarRulesSmartListField {...comp} />}
                                </span>
                            ))}
                        </div>
                    </MainDialogContainer>

                </DialogContent>
                <StyledDialogActions>
                    {!!okButtonDefinition && <Tooltip title={_T("Confirm")}><Button
                        id="ok_dialog_btn"
                        onClick={handleOkClick}
                        disabled={anyErrorExists}
                        data-testid="confirm-ok-btn"
                        variant="contained"
                        color="primary"
                    >
                        {_T("Ok")}
                    </Button></Tooltip>
                    }
                    <Tooltip title={subAddFieldDialogOpened ? _T("Cancel") : _T("Close")}><Button
                        id={subAddFieldDialogOpened ? "cancel_dialog_btn" : "close_dialog_btn"}
                        onClick={handleCloseClick}
                        color="primary"
                        data-testid="confirm-close-btn"
                        variant="contained"
                    >
                        {subAddFieldDialogOpened ? _T("Cancel") : _T("Close")}
                    </Button>
                    </Tooltip>
                </StyledDialogActions>
            </StyledDialogWithBackdropClick>
        </DialogOverlay> : null


};
